import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Home, PlusCircle, List, Star, MessageSquare, Tag, Image,
  Users, ShoppingCart, RefreshCw, Menu, X
} from "lucide-react";

import AddProduct from "./AddProduct";
import AllProducts from "./AllProducts";
import Reviews from "./Reviews";
import MarkBestSellers from "../pages/MarkBestSellers";
import SetOffer from "./SetOffer";
import HeroManager from "./HeroManager";
import AdminOrders from "./AdminOrders";
import AdminReturnRequests from "./AdminReturnRequests";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const adminToken = localStorage.getItem("adminToken");
    if (!adminToken) navigate("/admin/login");
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  const TABS = [
    { id: "dashboard", label: "Dashboard", icon: <Home size={18} /> },
    { id: "addproduct", label: "Add Product", icon: <PlusCircle size={18} /> },
    { id: "allproducts", label: "All Products", icon: <List size={18} /> },
    { id: "markbestsellers", label: "Best Sellers", icon: <Star size={18} /> },
    { id: "reviews", label: "Reviews", icon: <MessageSquare size={18} /> },
    { id: "setoffer", label: "Set Offer", icon: <Tag size={18} /> },
    { id: "heroslider", label: "Hero Slider", icon: <Image size={18} /> },
    { id: "clients", label: "Clients", icon: <Users size={18} /> },
    { id: "orders", label: "Orders", icon: <ShoppingCart size={18} /> },
    { id: "returns", label: "Return Requests", icon: <RefreshCw size={18} /> },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case "addproduct": return <AddProduct />;
      case "allproducts": return <AllProducts />;
      case "reviews": return <Reviews />;
      case "markbestsellers": return <MarkBestSellers />;
      case "setoffer": return <SetOffer />;
      case "heroslider": return <HeroManager />;
      case "orders": return <AdminOrders />;
      case "returns": return <AdminReturnRequests />;
      case "clients":
        return (
          <div className="p-8 text-center">
            <h2 className="text-2xl font-bold">Clients</h2>
            <p className="text-gray-500 mt-2">Coming soon...</p>
          </div>
        );
      default:
        return (
          <div className="p-8">
            <h2 className="text-3xl font-bold text-gray-800">Dashboard Overview</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              <div className="bg-white border rounded-xl p-6 shadow-sm">
                <h3 className="text-4xl font-bold text-gray-900">48</h3>
                <p className="text-gray-600 mt-2">Total Products</p>
              </div>

              <div className="bg-white border rounded-xl p-6 shadow-sm">
                <h3 className="text-4xl font-bold text-gray-900">892</h3>
                <p className="text-gray-600 mt-2">Happy Customers</p>
              </div>

              <div className="bg-white border rounded-xl p-6 shadow-sm">
                <h3 className="text-4xl font-bold text-gray-900">₹50L+</h3>
                <p className="text-gray-600 mt-2">Total Revenue</p>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="font-[Roboto] bg-gray-100 min-h-screen flex">

      {/* MOBILE TOP BAR */}
      <div className="md:hidden fixed top-0 w-full bg-white shadow p-4 z-50 flex justify-between">
        <h1 className="font-bold text-lg text-gray-800">Admin Panel</h1>
        <button onClick={() => setSidebarOpen(true)}>
          <Menu size={26} />
        </button>
      </div>

      {/* SIDEBAR */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-white border-r shadow-lg z-50 
        transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} 
        md:translate-x-0 transition-transform duration-300`}>

        {/* Sidebar Header */}
        <div className="p-6 border-b flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-800">Admin</h1>
          <button className="md:hidden" onClick={() => setSidebarOpen(false)}>
            <X size={24} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm transition 
                ${activeTab === tab.id
                  ? "bg-gray-900 text-white"
                  : "text-gray-700 hover:bg-gray-200"
                }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="w-full mt-6 bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-semibold"
          >
            Logout
          </button>
        </nav>
      </aside>

      {/* MAIN AREA */}
      <main className="flex-1 md:ml-64 p-4 mt-16 md:mt-0">
        <div className="bg-white shadow-sm border rounded-xl p-6 min-h-screen">
          {renderContent()}
        </div>
      </main>
    </div>
  );
}
